/**
 * Plain-language title/subtitle for enrichment jobs in Inbox lists.
 */

const ADD_KNOWLEDGE_PREFIX = /^add knowledge\s*[-\u2014]\s*/i;

function collapseWs(s) {
  return String(s || "")
    .replace(/\s+/g, " ")
    .trim();
}

function parseJobPayload(job) {
  const raw = job?.payload;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function parseJobResultDoc(job) {
  const raw = job?.result;
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function parseJobResultPreview(job) {
  const preview = parseJobResultDoc(job).preview;
  return preview && typeof preview === "object" ? preview : {};
}

function stripAddKnowledgePrefix(label) {
  const s = collapseWs(label);
  if (!s) return "";
  return collapseWs(s.replace(ADD_KNOWLEDGE_PREFIX, ""));
}

function sourceLabelFromSources(sources) {
  if (!Array.isArray(sources)) return "";
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const kind = String(src.kind || "").toLowerCase();
    const label = stripAddKnowledgePrefix(src.label);
    if (kind === "document" && label) return label;
  }
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const label = stripAddKnowledgePrefix(src.label);
    if (label && !/^pasted text$/i.test(label) && !/^snippet$/i.test(label)) {
      return label;
    }
  }
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const text = collapseWs(src.text);
    if (text) {
      return text.length <= 48 ? text : `${text.slice(0, 47)}...`;
    }
  }
  return "";
}

export function anchorNameFromEnrichmentJob(job) {
  const payload = parseJobPayload(job);
  const preview = parseJobResultPreview(job);
  return (
    collapseWs(payload.anchor_display_name) ||
    collapseWs(payload.anchor_matched_name_at_queue) ||
    collapseWs(preview.anchor_name) ||
    ""
  );
}

export function sourceLabelFromEnrichmentJob(job) {
  const payload = parseJobPayload(job);
  const fromSources = sourceLabelFromSources(payload.sources);
  if (fromSources) return fromSources;

  const name = collapseWs(job?.name);
  if (!name) return "";
  const parts = name.split(" \u00b7 ").map((p) => collapseWs(p)).filter(Boolean);
  if (parts.length >= 2) {
    const last = stripAddKnowledgePrefix(parts[parts.length - 1]);
    if (last) return last;
  }
  return "";
}

export function enrichmentJobCardPresentation(job) {
  const aboutApp = anchorNameFromEnrichmentJob(job);
  const sourceLabel = sourceLabelFromEnrichmentJob(job);
  const jt = String(job?.job_type || "").toLowerCase();
  const payload = parseJobPayload(job);

  if (jt === "entity_refine") {
    const title =
      collapseWs(payload.job_display_name) ||
      (aboutApp ? `AI recommendations · ${aboutApp}` : "AI recommendations");
    const subtitleParts = [];
    const keys = Array.isArray(payload.section_keys) ? payload.section_keys : [];
    if (keys.length === 1) {
      subtitleParts.push(String(keys[0]).replace(/_/g, " "));
    } else if (keys.length > 1) {
      subtitleParts.push(`${keys.length} sections`);
    }
    if (aboutApp && !title.includes(aboutApp)) {
      subtitleParts.push(`About ${aboutApp}`);
    }
    return {
      title,
      subtitleParts,
      aboutApp,
      sourceLabel: "",
    };
  }

  if (jt === "knowledge_ingestion") {
    const title =
      collapseWs(payload.job_display_name) ||
      sourceLabel ||
      (aboutApp ? `Indexed · ${aboutApp}` : "Indexed knowledge");
    const subtitleParts = ["Indexed for search — no extraction"];
    if (aboutApp && !title.includes(aboutApp)) {
      subtitleParts.push(`About ${aboutApp}`);
    }
    const doc = parseJobResultDoc(job);
    const evidence =
      doc.evidence && typeof doc.evidence === "object" ? doc.evidence : null;
    const fragments = Number(evidence?.fragment_count || 0);
    if (fragments > 0) {
      subtitleParts.push(
        `${fragments} passage${fragments === 1 ? "" : "s"}`
      );
    }
    return {
      title,
      subtitleParts,
      aboutApp,
      sourceLabel,
    };
  }

  let title = sourceLabel || aboutApp || collapseWs(job?.name) || "Knowledge upload";
  if (title === aboutApp && /^pasted text$/i.test(title)) {
    title = aboutApp ? `${aboutApp} - pasted notes` : "Pasted notes";
  }

  const subtitleParts = [];
  if (aboutApp && aboutApp !== title) {
    subtitleParts.push(`About ${aboutApp}`);
  }

  return {
    title,
    subtitleParts,
    aboutApp,
    sourceLabel,
  };
}

export function formatEnrichmentJobSubtitle(job, { formatTime, runParts } = {}) {
  const { subtitleParts } = enrichmentJobCardPresentation(job);
  const parts = [...subtitleParts];

  const ts = job?.created_at || job?.updated_at;
  if (typeof formatTime === "function" && ts) {
    parts.push(formatTime(ts));
  }

  if (runParts?.label && runParts?.duration) {
    parts.push(`${runParts.label} ${runParts.duration}`);
  }

  return parts.join(" \u00b7 ");
}

/** Map-language display labels (Approved/Proposed/etc.). */
export function formatItemApprovalLabel(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  if (value === "Approved") return "On map";
  if (value === "Proposed") return "Pending";
  return value;
}

/** Catalog status label for entity list + detail (matches explorer badges). */
export function formatEntityMapStatusLabel(entity) {
  const approvalRaw = String(
    entity?.approval_status || entity?.approvalStatus || ""
  ).trim();
  const lower = approvalRaw.toLowerCase();
  if (lower === "proposed") {
    return entity?.awaiting_baseline_approval
      ? "Awaiting approval"
      : "Pending";
  }
  if (lower === "approved") return "On map";
  if (approvalRaw) return formatItemApprovalLabel(approvalRaw);
  return "";
}

export function formatRelationshipStatusLabel(status) {
  const value = String(status || "").trim();
  if (!value) return "";
  if (value === "Approved" || value === "Implemented" || value === "Active")
    return "Active";
  if (value === "Proposed" || value === "Pending") return "Pending";
  if (value === "Rejected") return "Rejected";
  if (value === "Deprecated") return "Deprecated";
  return value;
}

export function formatFieldLabel(key, { mapLanguage = false } = {}) {
  const normalized = String(key || "").toLowerCase();
  if (!mapLanguage) return null;
  if (normalized === "approval_status" || normalized === "approvalstatus") {
    return "Map status";
  }
  return null;
}

export function formatFieldDisplayValue(key, value, { mapLanguage = false } = {}) {
  if (value === undefined || value === null || value === "") return value;
  if (!mapLanguage) return value;
  const normalized = String(key || "").toLowerCase();
  if (normalized === "approval_status" || normalized === "approvalstatus") {
    return formatItemApprovalLabel(value);
  }
  return value;
}
